<template>
  <div
    ref="selectComponent"
    class="pe-select relative box-border rounded-md border border-solid text-sm"
    :class="{ 
      'pointer-events-none cursor-not-allowed': disabled,
      active: showDropdown && !disabled
    }"
  >
    <div class="flex p-3" @click="toggleDropdown">
      <div class="grow truncate whitespace-nowrap">{{ selected }}</div>
      <div class="pe-select-caret ml-2">
        <UiIcon icon="caret-down" />
      </div>
    </div>
    <transition
      enter-active-class="transition ease-out duration-300 transform"
      enter-from-class="opacity-0 -translate-y-10 scale-y-50"
      enter-to-class="opacity-100 translate-y-0 scale-100"
      leave-active-class="ease-in duration-200"
      leave-from-class="opacity-100 translate-y-0 scale-100"
      leave-to-class="opacity-0 -translate-y-10 translate-y-0 scale-y-50"
    >
      <div
        v-if="showDropdown"
        class="absolute z-10 mt-2 w-full origin-top-right overflow-x-hidden overflow-y-auto whitespace-nowrap rounded-md bg-white shadow-md
        dark:bg-neutral-700"
        :style="{ 'max-height': `${height || 240}px` }"
      >
        <div v-show="visibleItems.length === 0" class="overflow-hidden text-ellipsis p-2">
          {{ $t('feedback.noFound') }}
        </div>
        <div
          v-show="filterable"
          class="flex items-center border-0 border-b border-solid border-neutral-300"
        >
          <input
            v-model="quickSearch"
            ref="quickSearchInput"
            type="text"
            class="mx-2 my-4 box-border grow rounded-md border border-neutral-900 px-2 py-1 text-base 
            dark:bg-neutral-700 dark:text-neutral-200 dark:placeholder:text-neutral-200"
            :placeholder="$t('input.search')"
          />
          <div class="mr-2 cursor-pointer py-4 px-2" @click="quickSearch = ''">
            <pe-icon>close</pe-icon>
          </div>
        </div>
        <div
          v-for="(item, i) in visibleItems"
          :key="i"
          class="overflow-hidden text-ellipsis p-2 hover:bg-neutral-300 dark:hover:bg-neutral-600 hover:cursor-pointer w-full"
          @click="onSelect(item)"
        >
          <span class="option-text">{{ item.label }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import UiIcon from '@/components/ui/icon.vue'

interface Option {
  label: string
  value: any
  selected?: any
}

const props = defineProps<{
  modelValue?: string | number | object | boolean
  placeholder?: string
  data?: Option[]
  height?: number
  filterable?: boolean
  disabled?: boolean 
}>()

const emit = defineEmits(['onSelect', 'update:modelValue'])

const selectComponent = ref(null) // armazena informações relacionadas a click exterior
const showDropdown = ref(false)
const quickSearch = ref('')
const quickSearchInput = ref<HTMLInputElement>()

const visibleItems = computed(() =>
  quickSearch.value
    ? props.data.filter((item) => item.label.toLocaleLowerCase()?.includes(quickSearch.value.toLocaleLowerCase()))
    : props.data
)
const selected = computed(() => {
  let label = ''
  if ((props.modelValue || props.modelValue >= 0) && props.data) label = props.data.find((e) => e.value === props.modelValue)?.label

  return label || props.placeholder || ''
})

const toggleDropdown = () => {
  showDropdown.value = !showDropdown.value
  if (showDropdown.value) {
    setTimeout(() => {
      quickSearchInput.value.focus()
    }, 200)
  }
}

const closeDropdown = () => {
  showDropdown.value = false
}

const onSelect = (opt) => {
  emit('update:modelValue', opt.value)
  emit('onSelect', opt)
  closeDropdown()
}

/**
 * Auto-esconde o componente quando o usuário clica fora do elemento
 * @param event
 */
const clickOutside = (e) => {
  if (showDropdown.value && !selectComponent.value.contains(e.target)) {
    closeDropdown()
  }
}

onMounted(() => {
  document.addEventListener('click', clickOutside, false)
})

onUnmounted(() => {
  document.removeEventListener('click', clickOutside, false)
})
</script>

<style lang="scss" scoped>
.pe-select {
  @apply border-black dark:border-neutral-600 border-solid;
  .pe-select-caret {
    transition: all 0.2s linear;
    transform: rotateZ(0deg);
  }
  &.active {
    .pe-select-caret {
      transform: rotateZ(-180deg);
    }
  }
}

.error .pe-select {
  @apply border-red-400;
}
</style>
